import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { BookOpen, Sparkles, Users, TrendingUp, Crown, Shield, ShieldCheck, Headphones, Moon, Languages, Film, Mic, BadgeCheck, Mic2, Wand2, Download, CheckCircle2, XCircle, HelpCircle, ArrowRight, Brush, Share2, Upload, GraduationCap, ShoppingCart, Compass, Sprout, Rocket, Trophy, Video, FileText, Mail, Image as ImageIcon } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import BookCardCover from '../components/BookCardCover';
import InstallAppButton from '../components/InstallAppButton';
import LeaderboardSection from '../components/LeaderboardSection';
import BrandWordmark from '../components/BrandWordmark';
import LandingAnnouncementsTeaser from '../components/LandingAnnouncementsTeaser';
import HoverCount from '../components/HoverCount';
import CostCalculator from '../components/CostCalculator';
// Phase 119.13 - ExplorerHighlight no longer used on this page; the
// "Why Creators Love Us" hero now carries the Explorer CTA. Kept
// available on Subscription.js still.
import FeaturedVideoStage from '../components/FeaturedVideoStage';
import { track } from '../lib/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [featuredBooks, setFeaturedBooks] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  // Phase 117 - Featured Academy cards on the landing page. AEO/GEO
  // signal: surfaces top parenting content + internal links from the
  // homepage body so crawlers find Academy without traversing the nav.
  const [featuredAcademy, setFeaturedAcademy] = useState([]);
  // Phase 117.6 - Homepage FAQ items pulled from the same admin-editable
  // `/api/faq` endpoint that powers the `/faq` page. Admins can edit
  // the source-of-truth FAQ once and BOTH /faq AND the homepage FAQ
  // section stay in sync. We render the first 6 Q&As across sections
  // for the homepage teaser; the "See all FAQs" CTA links to /faq.
  const [homepageFaq, setHomepageFaq] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  // Phase 95 - public platform-wide totals (storybooks / chapters /
  // illustrations) for the "AI-Powered Creation" hover-count stats strip.
  //
  // Phase 117 - initial state seeded with conservative *floor* values
  // (always <= the true current totals) so that JS-rendering crawlers
  // (Perplexity browse-mode, Googlebot Smartphone) NEVER snapshot the
  // page with "0+ stories" - they snapshot 400+/2000+/1400+ until the
  // real /api/platform/stats fetch (≈400 ms) refines the numbers. This
  // is the single biggest GEO/AEO fix from the Feb-2026 audit.
  const [platformStats, setPlatformStats] = useState(() => {
    // window.__BYB_INITIAL_STATS__ is injected by index.html if a
    // build-time stats snapshot is available; otherwise fall back to
    // the conservative floor that is always true.
    const injected =
      (typeof window !== 'undefined' && window.__BYB_INITIAL_STATS__) || null;
    return injected || {
      total_books: 400,
      total_chapters: 2000,
      total_illustrations: 1400,
    };
  });
  // Footer contact links - editable by admin from the dashboard and
  // served via /api/settings/contact. Falls back to the hard-coded
  // defaults if the fetch fails so the footer never looks broken.
  const [contact, setContact] = useState({
    instagram_url: 'https://www.instagram.com/buildyourbook.in/',
    youtube_url: 'https://www.youtube.com/@MyStorybooksbyBYB',
    facebook_url: '',
    twitter_url: 'https://x.com/BuildYourBookIN',
    pinterest_url: 'https://www.pinterest.com/BuildYourBook_Pins/',
    support_email: 'buildyourbook0@gmail.com',
  });

  useEffect(() => {
    fetchFeaturedBooks();
    // Live marketplace category counts. Re-fetch every 5 min so freshly-
    // approved books surface in the category pills without a reload.
    let cancelled = false;
    const loadStats = async () => {
      try {
        const { data } = await axios.get(`${API}/marketplace/stats`);
        if (cancelled) return;
        const map = {};
        (data.categories || []).forEach((c) => {
          map[(c.category || '').toLowerCase()] = c.count;
        });
        setCategoryCounts(map);
      } catch {
        /* non-critical, leave counts empty */
      }
    };
    loadStats();
    const id = setInterval(loadStats, 5 * 60 * 1000);

    // Phase 95 - fetch platform-wide totals once on mount for the
    // AI-Powered Creation stats strip. Re-fetch alongside marketplace
    // stats every 5 min so the numbers keep climbing without a reload.
    const loadPlatform = async () => {
      try {
        const { data } = await axios.get(`${API}/platform/stats`);
        if (!cancelled && data) setPlatformStats(data);
      } catch {
        /* keep zeros - the strip will simply show 0+ until next tick */
      }
    };
    loadPlatform();
    const pid = setInterval(loadPlatform, 5 * 60 * 1000);

    // Fetch editable contact links in parallel - non-blocking; the
    // defaults above stay visible until this resolves.
    axios
      .get(`${API}/settings/contact`)
      .then((r) => { if (!cancelled && r.data) setContact((c) => ({ ...c, ...r.data })); })
      .catch(() => { /* keep defaults */ });

    // Phase 117 - top 3 published Academy articles for the homepage
    // "Featured from the Academy" card grid. Non-blocking; if the
    // endpoint hiccups, the section simply doesn't render.
    axios
      .get(`${API}/academy?limit=3`)
      .then((r) => {
        if (!cancelled && r.data && Array.isArray(r.data.items)) {
          setFeaturedAcademy(r.data.items.slice(0, 3));
        }
      })
      .catch(() => { /* non-critical */ });

    // Phase 117.6 - Homepage FAQ pulled from /api/faq (admin-editable
    // via /api/admin/faq, same source of truth as the /faq page). We
    // flatten the first 6 items across all sections; if the endpoint
    // hiccups we fall back to the hardcoded teaser FAQs so the section
    // never disappears for users or crawlers.
    axios
      .get(`${API}/faq`)
      .then((r) => {
        const sections = r?.data?.sections;
        if (!cancelled && Array.isArray(sections)) {
          const flat = [];
          for (const section of sections) {
            for (const item of (section.items || [])) {
              if (item && item.q && item.a) {
                flat.push({ q: item.q, a: item.a, section: section.title });
                if (flat.length >= 6) break;
              }
            }
            if (flat.length >= 6) break;
          }
          if (flat.length > 0) setHomepageFaq(flat);
        }
      })
      .catch(() => { /* keep hardcoded fallback */ });

    return () => { cancelled = true; clearInterval(id); clearInterval(pid); };
  }, []);

  // Phase 116 - Live trust counters as Schema.org structured data.
  // Crawlers (Google, Perplexity, ChatGPT, Claude, Grok) that execute
  // JS get real platform totals via JSON-LD `interactionStatistic`
  // entries - the same numbers the HoverCount strip shows - rather
  // than the placeholder "0+" the static index.html would imply.
  useEffect(() => {
    const hasStats =
      platformStats.total_books > 0 ||
      platformStats.total_chapters > 0 ||
      platformStats.total_illustrations > 0;
    if (!hasStats) return undefined;
    const id = 'byb-platform-stats-jsonld';
    let node = document.getElementById(id);
    if (!node) {
      node = document.createElement('script');
      node.type = 'application/ld+json';
      node.id = id;
      document.head.appendChild(node);
    }
    node.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url: 'https://buildyourbook.in/',
      name: 'Build Your Book',
      description:
        'AI-powered personalized storybook marketplace for children in 16 languages.',
      interactionStatistic: [
        {
          '@type': 'InteractionCounter',
          interactionType: 'https://schema.org/CreateAction',
          name: 'Storybooks created',
          userInteractionCount: platformStats.total_books,
        },
        {
          '@type': 'InteractionCounter',
          interactionType: 'https://schema.org/WriteAction',
          name: 'Chapters generated',
          userInteractionCount: platformStats.total_chapters,
        },
        {
          '@type': 'InteractionCounter',
          interactionType: 'https://schema.org/PaintAction',
          name: 'Illustrations generated',
          userInteractionCount: platformStats.total_illustrations,
        },
      ],
    });
    return undefined;
  }, [platformStats]);

  const fetchFeaturedBooks = async () => {
    try {
      const response = await axios.get(`${API}/storybooks/featured`);
      setFeaturedBooks(response.data);
    } catch (error) {
      // eslint-disable-next-line no-unused-vars -- featured-books fetch is
      // non-critical; a toast on the public landing page would be jarring
      // for a first-time visitor who hasn't opted-in to anything yet.
      void error;
    }
  };

  const categories = [
    { name: 'Kids', icon: '🧒', color: '#A3E4D7' },
    { name: 'Fantasy', icon: '🦄', color: '#FDE68A' },
    { name: 'Moral Stories', icon: '⭐', color: '#C2410C' },
    { name: 'Regional Stories', icon: '🌍', color: '#A3E4D7' },
    { name: 'Adventure', icon: '🏔️', color: '#FDE68A' },
    { name: 'Educational', icon: '📚', color: '#C2410C' }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FFFDF9' }}>
      <SEOHead
        title="Build Your Book — AI-Powered Personalised Storybooks for Kids · 16 Languages"
        description="Turn your child into the hero of their own AI-generated storybook in 5 minutes. 16 languages, voice cloning, cinematic MP4, Kindle export. First book free. Triple-checked child-safe. Made in India, loved worldwide."
        canonical="https://buildyourbook.in/"
        ogImage="https://buildyourbook.in/og-cover.jpg"
        ogType="website"
        jsonLd={[{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://buildyourbook.in/#org',
          name: 'Build Your Book',
          alternateName: ['BYB', 'BuildYourBook', 'BuildYourBook.in'],
          url: 'https://buildyourbook.in/',
          logo: 'https://buildyourbook.in/brand-logo.png',
          founder: { '@type': 'Person', name: 'Priyal Dhrumit Shah', jobTitle: 'Founder & CEO' },
          foundingLocation: { '@type': 'Place', name: 'Ankleshwar, Gujarat, India' },
          sameAs: [
            'https://www.instagram.com/buildyourbook.in/',
            'https://www.youtube.com/@MyStorybooksbyBYB',
            'https://x.com/BuildYourBookIN',
            'https://www.pinterest.com/BuildYourBook_Pins/',
          ],
          contactPoint: { '@type': 'ContactPoint', contactType: 'customer support', email: 'buildyourbook0@gmail.com' },
        }, {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          '@id': 'https://buildyourbook.in/#website',
          url: 'https://buildyourbook.in/',
          name: 'Build Your Book',
          publisher: { '@id': 'https://buildyourbook.in/#org' },
          potentialAction: {
            '@type': 'SearchAction',
            target: { '@type': 'EntryPoint', urlTemplate: 'https://buildyourbook.in/marketplace?q={search_term_string}' },
            'query-input': 'required name=search_term_string',
          },
        }]}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 w-full backdrop-blur-xl border-b" style={{ background: 'rgba(255, 253, 249, 0.8)', borderColor: '#F3E8E0' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-12 py-3 sm:py-4 flex justify-between items-center gap-2">
          {/* LEFT cluster - Logo + Get Started + Academy.
              Phase 118.15 - "Get Started" was previously docked on the
              far right of the desktop nav; visitors on every device
              now see it adjacent to the brand mark, which is the
              fastest possible conversion path. Sizes scale down on
              mobile so the 3 pills + logo never overlap below 360 px. */}
          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            <Link to="/" data-testid="logo-link" className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <BookOpen size={28} className="sm:w-8 sm:h-8" style={{ color: '#C2410C' }} />
              <BrandWordmark size="text-lg sm:text-2xl" />
            </Link>
            <Link to="/login" data-testid="header-login-link" className="shrink-0">
              <button
                className="inline-flex items-center justify-center rounded-full px-3 sm:px-6 py-1.5 sm:py-2.5 text-xs sm:text-base font-semibold btn-hover whitespace-nowrap"
                style={{ background: '#C2410C', color: 'white' }}
              >
                {t('nav.getStarted')}
              </button>
            </Link>
            <Link
              to="/academy"
              data-testid="academy-link"
              className="inline-flex items-center gap-1 sm:gap-2 rounded-full px-3 sm:px-6 py-1.5 sm:py-2.5 text-xs sm:text-base font-semibold btn-hover transition-transform duration-200 hover:scale-[1.03] whitespace-nowrap shrink-0"
              style={{
                background: 'linear-gradient(135deg, #9333EA 0%, #FF8E71 100%)',
                color: 'white',
              }}
            >
              <Sparkles size={14} className="sm:w-4 sm:h-4" />
              Academy
            </Link>
          </div>
          {/* RIGHT cluster - secondary nav. Hidden below md; on mobile
              the user reaches these via the bottom-of-page nav / drawer.
              Phase 118.15 - Get Started removed from this cluster; lives
              on the left now (see above). */}
          <nav className="hidden md:flex items-center gap-3 lg:gap-4 shrink-0">
            <Link to="/marketplace" data-testid="marketplace-link" className="text-sm lg:text-base font-medium whitespace-nowrap" style={{ color: '#4A5568' }}>{t('nav.marketplace')}</Link>
            <Link to="/subscription" data-testid="subscription-link" className="text-sm lg:text-base font-medium flex items-center gap-1 whitespace-nowrap" style={{ color: '#C2410C' }}>
              <Crown size={16} />
              {t('nav.subscription')}
            </Link>
            <LanguageSwitcher />
            <InstallAppButton />
          </nav>
        </div>
      </header>

      {/* Hero Section - Magical Story Universe background */}
      {/*
        Full-width background image with the right-anchored magical storybook
        visual (book + castle + characters). Left 45–50% gets a soft cream
        gradient overlay so the dark heading + body text never sit on top
        of bright details from the artwork.

        Layout:
          • Desktop / tablet: grid 12-cols → text spans 7, visual area on
            the right keeps breathing room. `background-position: right
            center` keeps the book + castle anchored on the right edge
            even on ultra-wide screens.
          • Mobile (< 768px): switch to `background-position: center bottom`
            and add a tall vertical gradient so the book stays visible
            *below* the text without bleeding through it.

        Performance: a single `<img>` with `loading="eager"` + `fetchpriority="high"`
        warms the browser cache before paint; the actual decorative
        rendering uses `background-image` so it scales without layout
        shifts. The image itself is hosted on Emergent's CDN (already
        compressed WebP-equivalent).

        Animations:
          • `hero-float`: 6 s ease-in-out on the background - barely
            perceptible 8 px lift, signals "alive" without distracting.
          • `respects prefers-reduced-motion` - the keyframes resolve to
            zero translation when the user opts out.
      */}
      <style>{`
        @keyframes heroFloat {
          0%, 100% { background-position: right center; }
          50% { background-position: right calc(50% - 8px); }
        }
        @keyframes heroFloatMobile {
          0%, 100% { background-position: center 100%; }
          50% { background-position: center calc(100% - 6px); }
        }
        .hero-bg {
          /* Phase 119.14 - self-hosted WebP variants (~52 KB desktop /
             ~20 KB mobile) replace the 1.59 MB remote PNG. The browser
             picks the right size via image-set() URL fragments evaluated
             by min-width media queries below. Saves ~1.55 MB on LCP. */
          background-image: url('/api/assets/hero/hero-1920.webp');
          background-size: cover;
          background-position: right center;
          background-repeat: no-repeat;
          animation: heroFloat 6s ease-in-out infinite;
          transition: background-position 0.4s ease;
        }
        @media (max-width: 1279px) {
          .hero-bg { background-image: url('/api/assets/hero/hero-1280.webp'); }
        }
        @media (max-width: 767px) {
          .hero-bg { background-image: url('/api/assets/hero/hero-640.webp'); }
        }
        .hero-overlay {
          background: linear-gradient(
            to right,
            rgba(255, 246, 237, 0.97) 0%,
            rgba(255, 246, 237, 0.85) 35%,
            rgba(255, 246, 237, 0.45) 55%,
            rgba(255, 246, 237, 0) 75%
          );
        }
        @media (max-width: 767px) {
          .hero-bg {
            background-position: center bottom;
            background-size: 200% auto;
            animation: heroFloatMobile 6s ease-in-out infinite;
          }
          .hero-overlay {
            background: linear-gradient(
              to bottom,
              rgba(255, 246, 237, 0.97) 0%,
              rgba(255, 246, 237, 0.95) 28%,
              rgba(255, 246, 237, 0.4) 55%,
              rgba(255, 246, 237, 0) 75%
            );
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-bg { animation: none !important; }
          .hero-accent-sparkle::before,
          .hero-accent-sparkle::after { animation: none !important; }
        }

        /* Phase 99 - magical "Hero" sparkle (fires every 8 s, CSS only).
           Two stars criss-cross the accent word with a soft twinkle; the
           gradient text fill itself shimmers in tandem so the magic feels
           cohesive instead of stuck-on. */
        .hero-accent-sparkle {
          position: relative;
          display: inline-block;
          background-size: 200% 100%;
          animation: heroAccentShimmer 8s ease-in-out infinite;
        }
        .hero-accent-sparkle::before,
        .hero-accent-sparkle::after {
          content: "✨";
          position: absolute;
          font-size: 0.55em;
          line-height: 1;
          opacity: 0;
          pointer-events: none;
          filter: drop-shadow(0 0 6px rgba(255, 200, 90, 0.9));
        }
        .hero-accent-sparkle::before {
          top: -0.05em;
          right: -0.55em;
          animation: heroSparkleA 8s ease-in-out infinite;
        }
        .hero-accent-sparkle::after {
          bottom: 0.05em;
          left: -0.45em;
          animation: heroSparkleB 8s ease-in-out infinite 0.6s;
        }
        @keyframes heroAccentShimmer {
          0%, 100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
        @keyframes heroSparkleA {
          0%, 70%, 100% { opacity: 0; transform: scale(0.6) rotate(0deg); }
          75%           { opacity: 1; transform: scale(1.15) rotate(15deg); }
          85%           { opacity: 0.6; transform: scale(0.9) rotate(-10deg); }
          95%           { opacity: 0; transform: scale(0.5) rotate(0deg); }
        }
        @keyframes heroSparkleB {
          0%, 65%, 100% { opacity: 0; transform: scale(0.5) rotate(0deg); }
          72%           { opacity: 0.9; transform: scale(1.05) rotate(-12deg); }
          82%           { opacity: 0.5; transform: scale(0.85) rotate(8deg); }
          92%           { opacity: 0; transform: scale(0.5) rotate(0deg); }
        }
      `}</style>
      <section
        className="hero-bg relative overflow-hidden"
        data-testid="hero-section"
        aria-label="Magical Story Universe hero"
      >
        {/* SEO/A11y preload hint - decorative for sighted users but
            carries the full ALT for crawlers and screen readers.
            Phase 119.14 - now points at the local 20 KB mobile WebP
            (already preloaded in index.html) so this <img> reuses the
            preload cache hit rather than firing a fresh network call. */}
        <img
          src="/api/assets/hero/hero-640.webp"
          alt="Build Your Book hero illustration - a child flying on a giant open storybook through a magical castle landscape with dragons, unicorns and rainbows, symbolizing personalized AI-illustrated children's stories at buildyourbook.in."
          className="sr-only"
          width="640"
          height="360"
          loading="eager"
          fetchPriority="high"
        />
        <div className="hero-overlay absolute inset-0 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 md:py-36 lg:py-44">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div
              className="animate-fade-in md:col-span-7 lg:col-span-6 pt-2 md:pt-0 pb-[55vw] md:pb-0"
              data-testid="hero-text-block"
            >
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.05] mb-5"
                style={{ fontFamily: 'Fredoka', color: '#2D3748' }}
              >
                {t('landing.heroTitle')}{' '}
                <span className="text-gradient hero-accent-sparkle">{t('landing.heroTitleAccent')}</span>{' '}
                {t('landing.heroTitleEnd')}
              </h1>
              <p
                className="text-lg sm:text-xl lg:text-2xl font-semibold leading-snug mb-4 max-w-xl"
                style={{
                  fontFamily: 'Fredoka',
                  background: 'linear-gradient(90deg, #FF8E71 0%, #F59E0B 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
                data-testid="hero-subtitle"
              >
                {t('landing.heroSubtitle')}
              </p>
              <p
                className="text-base lg:text-lg leading-relaxed mb-5 max-w-xl"
                style={{ color: '#3D4756' }}
                data-testid="hero-desc"
              >
                {t('landing.heroDesc')}
              </p>
              <p
                className="text-sm lg:text-base font-medium mb-8 max-w-xl leading-relaxed"
                style={{ color: '#4A5568' }}
                data-testid="hero-chips"
              >
                {t('landing.heroChips')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/login" data-testid="hero-create-button">
                  <button
                    className="inline-flex items-center justify-center rounded-full px-8 py-3.5 font-semibold btn-hover shadow-lg"
                    style={{ background: '#C2410C', color: 'white', boxShadow: '0 10px 30px rgba(194, 65, 12, 0.35)' }}
                  >
                    <Sparkles size={20} className="mr-2" />
                    {t('landing.createCTA')}
                  </button>
                </Link>
                <Link to="/marketplace" data-testid="hero-browse-button">
                  <button
                    className="inline-flex items-center justify-center rounded-full px-8 py-3.5 font-semibold border btn-hover"
                    style={{ background: 'rgba(255, 249, 240, 0.9)', color: '#2D3748', borderColor: '#F3E8E0', backdropFilter: 'blur(6px)' }}
                  >
                    {t('landing.browseCTA')}
                  </button>
                </Link>
              </div>
            </div>
            {/* Right column intentionally left empty - the magical scene
                lives in the background image. Reserved column space keeps
                the text from sliding under the visual on wide viewports. */}
            <div className="hidden md:block md:col-span-5 lg:col-span-6" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────
          Phase 117 - Trust Signals Strip
          ────────────────────────────────────────────────────────────
          AEO/GEO signal that AI search engines (Perplexity, ChatGPT,
          Claude, Grok) cite when answering "Is Build Your Book safe /
          legitimate / Indian / a real product?". Renders 6 pills:
          Trademark · Razorpay LIVE · DPDP-ready · 16 languages ·
          Triple-checked child-safe · Made in India. Every pill is a
          *machine-extractable claim* - short, bold, scannable.
          ──────────────────────────────────────────────────────────── */}
      <section
        className="py-10 border-y"
        style={{ background: '#FFF7ED', borderColor: '#F3E8E0' }}
        data-testid="trust-signals-strip"
        aria-label="Build Your Book trust signals"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-center text-xs sm:text-sm font-semibold uppercase tracking-wider mb-5"
             style={{ color: '#9A3412', letterSpacing: '0.08em' }}>
            Why parents trust Build Your Book
          </p>
          <ul className="flex flex-wrap justify-center items-center gap-3 sm:gap-4" data-testid="trust-pills">
            <li
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border shadow-sm"
              style={{ borderColor: '#F3E8E0', color: '#2D3748' }}
              data-testid="trust-pill-trademark"
            >
              <BadgeCheck size={16} style={{ color: '#9333EA' }} />
              <span className="text-sm font-semibold">
                Build&nbsp;Your&nbsp;Book<sup style={{ fontSize: '0.6em' }}>™</sup>
                <span className="hidden sm:inline"> &middot; Trademark protected</span>
              </span>
            </li>
            <li
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border shadow-sm"
              style={{ borderColor: '#F3E8E0', color: '#2D3748' }}
              data-testid="trust-pill-razorpay"
            >
              <span
                aria-hidden="true"
                className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white"
                style={{ background: '#0D2D74' }}
              >R</span>
              <span className="text-sm font-semibold">Razorpay &middot; INR &amp; USD</span>
            </li>
            <li
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border shadow-sm"
              style={{ borderColor: '#F3E8E0', color: '#2D3748' }}
              data-testid="trust-pill-dpdp"
            >
              <Shield size={16} style={{ color: '#065F46' }} />
              <span className="text-sm font-semibold">DPDP-ready &middot; India compliant</span>
            </li>
            <li
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border shadow-sm"
              style={{ borderColor: '#F3E8E0', color: '#2D3748' }}
              data-testid="trust-pill-languages"
            >
              <Languages size={16} style={{ color: '#9A3412' }} />
              <span className="text-sm font-semibold">16 languages</span>
            </li>
            <li
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border shadow-sm"
              style={{ borderColor: '#F3E8E0', color: '#2D3748' }}
              data-testid="trust-pill-safe"
            >
              <ShieldCheck size={16} style={{ color: '#1B5E20' }} />
              <Link to="/safety" className="text-sm font-semibold hover:underline" style={{ color: '#1B5E20' }}>
                Triple-checked &middot; 100% child-safe
              </Link>
            </li>
            <li
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border shadow-sm"
              style={{ borderColor: '#F3E8E0', color: '#2D3748' }}
              data-testid="trust-pill-india"
            >
              <span aria-hidden="true" className="text-base leading-none">🇮🇳</span>
              <span className="text-sm font-semibold">Made in India</span>
            </li>
          </ul>
          <p className="text-center text-xs mt-5" style={{ color: '#4A5568' }} data-testid="trust-fineprint">
            <strong>Build&nbsp;Your&nbsp;Book<sup style={{ fontSize: '0.65em' }}>™</sup></strong> is a
            registered consumer brand of <strong>OrcSyn Intelligent Technologies</strong>.
            Operated exclusively at{' '}
            <a
              href="https://buildyourbook.in/"
              className="font-semibold hover:underline"
              style={{ color: '#C2410C' }}
            >buildyourbook.in</a>.
          </p>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────
          Phase 117 - "How It Works" 3-step section
          ────────────────────────────────────────────────────────────
          GEO signal that AI engines extract when answering "How does
          Build Your Book work?". Each step is a verb + 3-word phrase
          + one-sentence explanation, optimized for AI-generated
          summarization (the format Perplexity, ChatGPT and Claude
          actually paraphrase).
          ──────────────────────────────────────────────────────────── */}
      <section
        className="py-14"
        data-testid="how-it-works-section"
        aria-label="How Build Your Book works in 3 steps"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2
              id="how-it-works"
              className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug mb-4"
              style={{ fontFamily: 'Fredoka', color: '#2D3748' }}
            >
              How does Build Your Book work?
            </h2>
            <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: '#4A5568' }}>
              A personalized AI storybook for your child, in under 5 minutes on{' '}
              <strong>buildyourbook.in</strong> - voice your idea, our AI illustrates &amp;
              narrates it, then you download the MP4 video and PDF.
            </p>
          </div>
          <ol
            className="grid md:grid-cols-3 gap-6 list-none"
            data-testid="how-it-works-steps"
          >
            <li
              className="bg-white rounded-3xl p-8 border relative"
              style={{ borderColor: '#F3E8E0', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}
              data-testid="how-step-1"
            >
              <div
                className="absolute -top-4 -left-4 w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #FF8E71, #F59E0B)', boxShadow: '0 6px 16px rgba(255, 142, 113, 0.4)' }}
                aria-hidden="true"
              >1</div>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#FBCFE8' }}>
                <Mic2 size={28} style={{ color: '#9D174D' }} />
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
                Voice your idea
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: '#4A5568' }}>
                Tell Build Your Book your child&apos;s name, age, language and the story you
                imagine - by voice or text. We polish the rest.
              </p>
            </li>
            <li
              className="bg-white rounded-3xl p-8 border relative"
              style={{ borderColor: '#F3E8E0', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}
              data-testid="how-step-2"
            >
              <div
                className="absolute -top-4 -left-4 w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #9333EA, #FF8E71)', boxShadow: '0 6px 16px rgba(147, 51, 234, 0.35)' }}
                aria-hidden="true"
              >2</div>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#DDD6FE' }}>
                <Wand2 size={28} style={{ color: '#5B21B6' }} />
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
                AI generates &amp; illustrates
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: '#4A5568' }}>
                Gemini 2.5 Flash writes the story, Gemini Nano Banana illustrates every
                chapter, OpenAI TTS narrates it. Every page is triple-checked child-safe.
              </p>
            </li>
            <li
              className="bg-white rounded-3xl p-8 border relative"
              style={{ borderColor: '#F3E8E0', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}
              data-testid="how-step-3"
            >
              <div
                className="absolute -top-4 -left-4 w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #6366F1, #1E3A8A)', boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)' }}
                aria-hidden="true"
              >3</div>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#BAE6FD' }}>
                <Download size={28} style={{ color: '#1E3A8A' }} />
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
                Download MP4 &amp; PDF
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: '#4A5568' }}>
                Read it as a flipbook, listen as an audiobook, or watch as a cinematic
                MP4. Keep the PDF forever. Re-create in any of 16 languages.
              </p>
            </li>
          </ol>

          {/* ────────────────────────────────────────────────────────
              Phase 117.12 - "The complete journey" strip.
              First-time visitors land here right after the 1-2-3
              boxes and need to see the full arc - not just creation,
              but everything Build Your Book unlocks AFTER they make
              a book (listen, watch, share, publish, earn, learn).
              The 10 verbs visualise the lifecycle as a chain so the
              page feels like an active product, not a static service
              brochure. Built with self-contained icon chips → easy
              for screen readers, AI crawlers AND humans to parse.
              ──────────────────────────────────────────────────────── */}
          <div
            className="mt-16 relative overflow-hidden rounded-3xl px-6 sm:px-10 py-10 sm:py-12"
            style={{
              background: 'linear-gradient(135deg, #2D1B69 0%, #1F0F3D 55%, #4C1D95 100%)',
              boxShadow: '0 30px 60px -28px rgba(45, 27, 105, 0.55)',
            }}
            data-testid="how-it-works-journey"
          >
            {/* Decorative blob behind the chips */}
            <div
              aria-hidden="true"
              className="absolute -top-16 -right-16 w-72 h-72 rounded-full opacity-20"
              style={{ background: 'radial-gradient(circle, #FF8E71 0%, transparent 70%)' }}
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full opacity-20"
              style={{ background: 'radial-gradient(circle, #FCD34D 0%, transparent 70%)' }}
            />

            <div className="relative text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold mb-4"
                   style={{ background: 'rgba(252, 211, 77, 0.18)', color: '#FCD34D', border: '1px solid rgba(252, 211, 77, 0.3)' }}>
                <Sparkles size={12} /> The complete journey
              </div>
              <p
                className="text-2xl sm:text-3xl mb-2"
                style={{ fontFamily: 'Fredoka', color: 'white', lineHeight: 1.25 }}
              >
                The complete <span style={{ color: '#FCD34D' }}>AI-powered family storytelling</span> journey.
              </p>
              <p className="text-sm sm:text-base" style={{ color: 'rgba(255,255,255,0.78)' }}>
                Where <em>imagination</em> becomes <em>stories</em>, <em>stories</em> become <em>memories</em>,
                and <em>creators</em> become <em>authors</em>.
              </p>
            </div>

            {/* Verb chain - 10 chips with arrow connectors */}
            <ol
              className="relative mt-8 flex flex-wrap items-center justify-center gap-x-2 gap-y-3 list-none"
              aria-label="Build Your Book lifecycle in 10 steps"
              data-testid="journey-verb-chain"
            >
              {[
                { verb: 'Imagine',    Icon: Sparkles,       tone: '#FBCFE8' },
                { verb: 'Create',     Icon: Wand2,          tone: '#DDD6FE' },
                { verb: 'Illustrate', Icon: Brush,          tone: '#FECACA' },
                { verb: 'Listen',     Icon: Headphones,     tone: '#FED7AA' },
                { verb: 'Watch',      Icon: Film,           tone: '#BAE6FD' },
                { verb: 'Share',      Icon: Share2,         tone: '#A7F3D0' },
                { verb: 'Download',   Icon: Download,       tone: '#FCD34D' },
                { verb: 'Publish',    Icon: Upload,         tone: '#FBA74D' },
                { verb: 'Earn',       Icon: TrendingUp,     tone: '#6EE7B7' },
                { verb: 'Learn',      Icon: GraduationCap,  tone: '#C4B5FD' },
              ].map((step, idx, arr) => (
                <li key={step.verb} className="flex items-center gap-2">
                  <span
                    className="inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold"
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      color: 'white',
                      border: `1px solid ${step.tone}55`,
                      boxShadow: `0 4px 14px ${step.tone}22`,
                      backdropFilter: 'blur(8px)',
                    }}
                    data-testid={`journey-step-${step.verb.toLowerCase()}`}
                  >
                    <step.Icon size={15} style={{ color: step.tone }} />
                    {step.verb}
                  </span>
                  {idx < arr.length - 1 && (
                    <ArrowRight size={14} style={{ color: 'rgba(255,255,255,0.4)' }} aria-hidden="true" />
                  )}
                </li>
              ))}
            </ol>

            <div className="relative mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/login"
                onClick={() => track('journey_cta_click', { surface: 'landing_journey_strip' })}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-transform hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #FF8E71 0%, #F59E0B 100%)', color: 'white', boxShadow: '0 12px 24px -8px rgba(245, 158, 11, 0.5)' }}
                data-testid="journey-cta-create"
              >
                Start your 1st storybook - it&apos;s on us <ArrowRight size={14} />
              </Link>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                No card needed · 16 languages · Triple-checked child-safe
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-14" style={{ background: '#FFF9F0' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug mb-4" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
              Why Creators Love Us
            </h2>
            <p className="text-base leading-relaxed" style={{ color: '#4A5568' }}>Everything you need to bring your stories to life</p>
          </div>
          <div className="space-y-8">
            {/* Phase 119.12 - Row 0: Explorer ₹50 "no-signup" hero card.
                Optimised for paid IG/FB ad landings - users who hit the
                Features section immediately see the cheap one-shot
                offer and a single, friction-free CTA to /create.
                Duplicates the headline benefits below the comparison
                table for click-conversion - intentional, the ad funnel
                is the primary use case. */}
            <div
              className="rounded-3xl p-8 sm:p-10 border relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #FFF7ED 0%, #FFE4D6 55%, #FECDA8 100%)',
                borderColor: '#F5B400',
                boxShadow: '0 14px 36px rgba(245, 180, 0, 0.25)',
              }}
              data-testid="explorer-feature-hero"
            >
              <div className="grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-8 lg:gap-12 items-center relative">
                {/* Left: pitch + CTA */}
                <div>
                  <div
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider mb-4"
                    style={{ background: '#2D3748', color: '#FDE68A' }}
                  >
                    <Compass size={12} />
                    Explorer plan - one-shot purchase
                  </div>
                  <h3
                    className="text-3xl sm:text-4xl font-bold tracking-tight mb-3 leading-tight"
                    style={{ fontFamily: 'Fredoka', color: '#2D3748' }}
                  >
                    Pay <span style={{ color: '#C2410C' }}>~₹50 / $0.5</span>.
                    Get your storybook in your email.
                  </h3>
                  <p className="text-base mb-2" style={{ color: '#4A5568' }}>
                    <strong style={{ color: '#92400E' }}>No account. No subscription. No card stored.</strong>
                    {' '}One-time payment, lifelong storybook.
                  </p>
                  <p className="text-sm mb-6" style={{ color: '#4A5568' }}>
                    Pick a child, a theme, a language. Our AI writes the story,
                    illustrates every chapter, narrates it in studio-grade audio,
                    and stitches everything into a polished MP4 video plus a
                    print-ready PDF flipbook - delivered straight to your inbox
                    in minutes.
                  </p>
                  <div className="flex items-center gap-3 flex-wrap mb-6">
                    <Link
                      to="/create"
                      data-testid="explorer-feature-hero-cta"
                      onClick={() => {
                        try {
                          track('explorer_hero_cta_click', {
                            surface: 'features_section',
                            page: 'landing',
                          });
                        } catch (_) { /* analytics opt-out - swallow */ }
                      }}
                      className="inline-flex items-center justify-center rounded-full px-7 py-3.5 text-base font-bold btn-hover"
                      style={{
                        background: 'linear-gradient(135deg, #F5B400 0%, #FF8E71 100%)',
                        color: 'white',
                        boxShadow: '0 8px 22px rgba(255, 142, 113, 0.55)',
                      }}
                    >
                      <Sparkles size={18} className="mr-2" />
                      Pay &amp; generate my storybook
                    </Link>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#92400E' }}>
                      <Shield size={12} /> Razorpay secured · UPI / cards / wallets
                    </span>
                  </div>
                  <p className="text-[11px]" style={{ color: '#92400E' }}>
                    Already have an account? <Link to="/login" className="underline font-semibold">Sign in</Link> to use your free trial or subscription instead.
                  </p>
                </div>

                {/* Right: deliverables grid - user POV, benefit-led */}
                <ul className="grid sm:grid-cols-2 gap-3" data-testid="explorer-feature-deliverables">
                  {[
                    { icon: Sparkles, accent: '#F5B400', strong: 'AI-personalised', tail: 'story with your child as the hero' },
                    { icon: ImageIcon, accent: '#EC4899', strong: '5 chapter illustrations', tail: '+ a custom cover image' },
                    { icon: Headphones, accent: '#8B5CF6', strong: 'TTS narration', tail: 'in male or female voice' },
                    { icon: Video, accent: '#3B82F6', strong: 'Full MP4 video', tail: 'emailed - share or play on any TV' },
                    { icon: FileText, accent: '#10B981', strong: 'Flipbook PDF', tail: 'emailed - print, gift, archive' },
                    { icon: Mail, accent: '#FB923C', strong: 'Delivered to your inbox', tail: 'no sign-in, no app to download' },
                  ].map(({ icon: I, accent, strong, tail }) => (
                    <li
                      key={strong}
                      className="flex items-start gap-3 rounded-2xl p-3"
                      style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(245,180,0,0.25)' }}
                    >
                      <span
                        aria-hidden="true"
                        className="inline-flex w-9 h-9 rounded-xl items-center justify-center shrink-0"
                        style={{ background: `${accent}22`, color: accent }}
                      >
                        <I size={18} strokeWidth={2.4} />
                      </span>
                      <span className="text-sm leading-snug" style={{ color: '#2D3748' }}>
                        <strong>{strong}</strong> {tail}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Row 1 - AI-Powered Creation (full-width horizontal) */}
            <div
              className="bg-white rounded-3xl p-8 sm:p-10 border"
              style={{ borderColor: '#F3E8E0', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}
              data-testid="ai-creation-card"
            >
              <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-8 lg:gap-12 items-start">
                <div>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: '#FDE68A' }}>
                    <Sparkles size={28} style={{ color: '#2D3748' }} />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-medium tracking-tight mb-3" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>AI-Powered Creation</h3>
                  <p className="text-base leading-relaxed" style={{ color: '#4A5568' }}>
                    Generate complete storybooks with AI assistance. Just provide your ideas, and watch magic happen.
                  </p>
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-4" data-testid="ai-creation-feature-list">
                  <li className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="relative inline-flex w-10 h-10 rounded-xl items-center justify-center shrink-0 shadow-md"
                      style={{
                        background: 'linear-gradient(135deg, #A7F3D0 0%, #34D399 100%)',
                        transform: 'perspective(200px) rotateX(12deg) rotateY(-6deg)',
                        boxShadow: '0 6px 14px rgba(52, 211, 153, 0.35), inset 0 1px 0 rgba(255,255,255,0.45)',
                      }}
                    >
                      <span
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm"
                        style={{ background: '#065F46' }}
                      >✓</span>
                      <Shield size={20} style={{ color: '#065F46' }} strokeWidth={2.5} />
                    </span>
                    <span className="text-sm leading-snug" style={{ color: '#2D3748' }}>
                      <strong>Triple-checked</strong> child-safe content guarantee
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="relative inline-flex w-10 h-10 rounded-xl items-center justify-center shrink-0 shadow-md"
                      style={{
                        background: 'linear-gradient(135deg, #FBCFE8 0%, #EC4899 100%)',
                        transform: 'perspective(200px) rotateX(12deg) rotateY(6deg)',
                        boxShadow: '0 6px 14px rgba(236, 72, 153, 0.4), inset 0 1px 0 rgba(255,255,255,0.45)',
                      }}
                    >
                      <Mic size={20} style={{ color: '#9D174D' }} strokeWidth={2.5} />
                      <span
                        className="absolute -bottom-0.5 right-1 w-1 h-2 bg-white/80 rounded-full animate-pulse"
                      />
                    </span>
                    <span className="text-sm leading-snug" style={{ color: '#2D3748' }}>
                      <strong>Voice your idea</strong> - dictate stories, AI polishes the rest
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="relative inline-flex w-10 h-10 rounded-xl items-center justify-center shrink-0 shadow-md"
                      style={{
                        background: 'linear-gradient(135deg, #DDD6FE 0%, #8B5CF6 100%)',
                        transform: 'perspective(200px) rotateX(12deg) rotateY(6deg)',
                        boxShadow: '0 6px 14px rgba(139, 92, 246, 0.35), inset 0 1px 0 rgba(255,255,255,0.45)',
                      }}
                    >
                      <Headphones size={20} style={{ color: '#5B21B6' }} strokeWidth={2.5} />
                      <span
                        className="absolute -bottom-0.5 -right-1 flex items-end gap-0.5"
                        style={{ height: '14px' }}
                      >
                        <span className="w-0.5 bg-white rounded-full animate-pulse" style={{ height: '6px' }} />
                        <span className="w-0.5 bg-white rounded-full animate-pulse" style={{ height: '10px', animationDelay: '0.15s' }} />
                        <span className="w-0.5 bg-white rounded-full animate-pulse" style={{ height: '4px', animationDelay: '0.3s' }} />
                      </span>
                    </span>
                    <span className="text-sm leading-snug" style={{ color: '#2D3748' }}>
                      <strong>Audio narration</strong> in Male, Female &amp; your own cloned voice
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="relative inline-flex w-10 h-10 rounded-xl items-center justify-center shrink-0 shadow-md"
                      style={{
                        background: 'linear-gradient(135deg, #1E3A8A 0%, #312E81 100%)',
                        transform: 'perspective(200px) rotateX(12deg) rotateY(-6deg)',
                        boxShadow: '0 6px 14px rgba(49, 46, 129, 0.45), inset 0 1px 0 rgba(255,255,255,0.25)',
                      }}
                    >
                      <Moon size={18} style={{ color: '#FCD34D' }} strokeWidth={2.5} fill="#FCD34D" />
                      <span
                        aria-hidden="true"
                        className="absolute top-1 left-1 text-[8px] text-white/80"
                        style={{ textShadow: '0 0 4px rgba(255,255,255,0.8)' }}
                      >★</span>
                      <span
                        aria-hidden="true"
                        className="absolute bottom-1 right-1.5 text-[6px] text-white/70"
                      >✦</span>
                    </span>
                    <span className="text-sm leading-snug" style={{ color: '#2D3748' }}>
                      <strong>Bedtime Mode</strong> - auto-narrate &amp; sleep timer
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="relative inline-flex w-10 h-10 rounded-xl items-center justify-center shrink-0 shadow-md"
                      style={{
                        background: 'linear-gradient(135deg, #FED7AA 0%, #F97316 100%)',
                        transform: 'perspective(200px) rotateX(12deg) rotateY(6deg)',
                        boxShadow: '0 6px 14px rgba(249, 115, 22, 0.35), inset 0 1px 0 rgba(255,255,255,0.45)',
                      }}
                    >
                      <Languages size={20} style={{ color: '#9A3412' }} strokeWidth={2.5} />
                    </span>
                    <span className="text-sm leading-snug" style={{ color: '#2D3748' }}>
                      <strong>16-language translation</strong> - Hindi, Gujarati, Spanish, French &amp; more
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="relative inline-flex w-10 h-10 rounded-xl items-center justify-center shrink-0 shadow-md"
                      style={{
                        background: 'linear-gradient(135deg, #BAE6FD 0%, #6366F1 100%)',
                        transform: 'perspective(200px) rotateX(12deg) rotateY(-6deg)',
                        boxShadow: '0 6px 14px rgba(99, 102, 241, 0.4), inset 0 1px 0 rgba(255,255,255,0.45)',
                      }}
                    >
                      <Film size={20} style={{ color: '#312E81' }} strokeWidth={2.5} />
                      <span
                        aria-hidden="true"
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white shadow-sm"
                        style={{ background: '#312E81' }}
                      >▶</span>
                    </span>
                    <span className="text-sm leading-snug" style={{ color: '#2D3748' }}>
                      <strong>Cinematic Video</strong> - with animated MP4 trailer
                    </span>
                  </li>
                </ul>
              </div>
              {/* Phase 95 - live platform totals (hover-animated count-up) */}
              <div className="mt-8 pt-6 border-t" style={{ borderColor: '#F3E8E0' }}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="ai-creation-stats-strip">
                  <HoverCount
                    testid="stat-storybooks"
                    value={platformStats.total_books}
                    label="Created Storybooks"
                  />
                  <HoverCount
                    testid="stat-chapters"
                    value={platformStats.total_chapters}
                    label="Generated Chapters"
                  />
                  <HoverCount
                    testid="stat-illustrations"
                    value={platformStats.total_illustrations}
                    label="Generated Illustrations"
                  />
                </div>
              </div>
            </div>

            {/* Row 2 - Global Marketplace (3 value props) + Creator Dashboard.
                Phase 118.20 - surfaces the three concrete ways our marketplace
                creates value: Buy/Gift (consumer), Publish & Earn (creator
                inbound), and KDP eBook export (global reach via Amazon). */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-3xl p-8 border" style={{ borderColor: '#F3E8E0', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: '#A3E4D7' }}>
                  <Users size={24} style={{ color: '#2D3748' }} />
                </div>
                <h3 className="text-xl sm:text-2xl font-medium tracking-tight mb-4" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>Global Marketplace</h3>
                <ul className="space-y-3 text-sm leading-relaxed" style={{ color: '#4A5568' }} data-testid="global-marketplace-value-props">
                  <li className="flex items-start gap-3">
                    <span
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ background: '#FFF7ED', color: '#C2410C' }}
                      aria-hidden="true"
                    >
                      <ShoppingCart size={14} />
                    </span>
                    <span>
                      <strong style={{ color: '#2D3748' }}>Buy or gift</strong> a storybook from{' '}
                      <Link to="/marketplace" className="underline font-semibold" style={{ color: '#C2410C' }} data-testid="value-prop-buy-link">
                        the marketplace
                      </Link>{' '}— curated, kid-safe stories for any age, language and occasion.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ background: '#FEF3C7', color: '#92400E' }}
                      aria-hidden="true"
                    >
                      <TrendingUp size={14} />
                    </span>
                    <span>
                      <strong style={{ color: '#2D3748' }}>Publish your own books</strong> to the marketplace and{' '}
                      <strong style={{ color: '#16A34A' }}>earn royalties</strong> every time someone buys
                      a copy of your storybook.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ background: '#FFE4E6', color: '#9F1239' }}
                      aria-hidden="true"
                    >
                      <BookOpen size={14} />
                    </span>
                    <span>
                      <strong style={{ color: '#2D3748' }}>Export to Kindle (.epub)</strong> and publish on{' '}
                      <strong>Amazon KDP</strong> - reach a global audience and keep{' '}
                      <strong style={{ color: '#16A34A' }}>100% of Amazon&apos;s 70% royalty</strong>.
                      Subscriber feature.
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-3xl p-8 border" style={{ borderColor: '#F3E8E0', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: '#FF8E71' }}>
                  <TrendingUp size={24} style={{ color: 'white' }} />
                </div>
                <h3 className="text-xl sm:text-2xl font-medium tracking-tight mb-3" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>Creator Dashboard</h3>
                <p className="text-base leading-relaxed" style={{ color: '#4A5568' }}>Flourish your idea into generating stories &amp; storytelling effortlessly. Also, can store all your creations for lifelong.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug mb-4" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
              Explore Story Categories
            </h2>
            <p className="text-base leading-relaxed" style={{ color: '#4A5568' }}>Find the perfect genre for your next adventure</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {categories.map((category) => {
              const count = categoryCounts[category.name.toLowerCase()] || 0;
              return (
                <button
                  key={`cat-${category.name}`}
                  data-testid={`category-${category.name.toLowerCase().replace(' ', '-')}`}
                  onClick={() => navigate(`/marketplace?category=${category.name}`)}
                  className="bg-white rounded-2xl p-6 border card-hover text-center"
                  style={{ borderColor: '#F3E8E0' }}
                >
                  <div className="text-4xl mb-3">{category.icon}</div>
                  <div className="text-sm font-semibold mb-2" style={{ color: '#2D3748' }}>{category.name}</div>
                  <div
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold"
                    style={{ background: '#FFF7ED', color: '#C2410C' }}
                    data-testid={`category-count-${category.name.toLowerCase().replace(' ', '-')}`}
                    title={count === 1 ? '1 storybook live' : `${count} storybooks live`}
                  >
                    {count} {count === 1 ? 'book' : 'books'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Books Section */}
      {featuredBooks.length > 0 && (
        <section className="py-14" style={{ background: '#FFF9F0' }}>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug mb-4" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
                Featured Storybooks
              </h2>
              <p className="text-base leading-relaxed" style={{ color: '#4A5568' }}>Discover magical tales from our talented creators</p>
            </div>

            {/* Phase 96 - kling.ai-style daily cinematic video showcase */}
            <FeaturedVideoStage />

            {/* Horizontal infinite-scroll marquee of the featured books.
                Cards are duplicated for a seamless loop; the outer
                `.featured-marquee-mask` pauses the animation on hover
                and applies a soft edge fade. Each card lifts on
                individual hover via `.featured-marquee-card`. */}
            <div className="featured-marquee-mask" data-testid="featured-books-marquee">
              <div className="featured-marquee-track py-2">
                {[...featuredBooks, ...featuredBooks].map((book, idx) => (
                  <Link
                    key={`${book.book_id}-${idx}`}
                    to={`/book/${book.book_id}`}
                    data-testid={`featured-book-${book.book_id}-${idx}`}
                    className="featured-marquee-card bg-white rounded-3xl border overflow-hidden shrink-0"
                    style={{
                      width: 'min(320px, 80vw)',
                      borderColor: '#F3E8E0',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)',
                    }}
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <BookCardCover book={book} />
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg sm:text-xl font-medium mb-2 line-clamp-1" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>{book.title}</h3>
                      <p className="text-sm leading-normal mb-4 line-clamp-2" style={{ color: '#718096' }}>{book.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold" style={{ background: '#FDE68A', color: '#2D3748' }}>
                          {book.category}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ────────────────────────────────────────────────────────────
          Phase 117 - Featured from the Academy (top 3 articles)
          ────────────────────────────────────────────────────────────
          Surfaces 3 evergreen Academy articles from the homepage body
          (not just the nav) so crawlers find topical context and AI
          engines understand Build Your Book covers parenting expertise
          + storytelling, not just the marketplace. Each card has a
          Wikipedia-link "research depth" indicator where applicable.
          ──────────────────────────────────────────────────────────── */}
      {featuredAcademy.length > 0 && (
        <section
          className="py-14"
          style={{ background: '#FFF9F0' }}
          data-testid="featured-academy-section"
          aria-label="Featured articles from Build Your Book Academy"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug mb-3"
                  style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
                Learn from Grandma Jayshree&apos;s Academy
              </h2>
              <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: '#4A5568' }}>
                Free parenting wisdom from <strong>Build Your Book</strong> on{' '}
                <a href="https://buildyourbook.in/academy" className="hover:underline" style={{ color: '#C2410C', fontWeight: 600 }}>
                  buildyourbook.in/academy
                </a>{' '}— screen time, picky eating, emotional development, and how to
                turn any night into a story night.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {featuredAcademy.map((article) => (
                <Link
                  key={article.content_id || article.slug}
                  to={`/academy/${article.category}/${article.slug}`}
                  data-testid={`featured-academy-card-${article.slug}`}
                  className="bg-white rounded-3xl border overflow-hidden card-hover flex flex-col"
                  style={{ borderColor: '#F3E8E0', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}
                >
                  <div
                    className="h-40 flex items-center justify-center text-white px-4"
                    style={{
                      background: article.type === 'how_to_guide'
                        ? 'linear-gradient(135deg, #9333EA, #FF8E71)'
                        : 'linear-gradient(135deg, #FF8E71, #F59E0B)',
                    }}
                  >
                    <span
                      className="inline-flex items-center gap-2.5 rounded-full px-5 py-2 bg-white/25 backdrop-blur-md text-lg font-semibold uppercase tracking-wide text-white"
                      style={{ fontFamily: 'Fredoka' }}
                    >
                      {article.type === 'how_to_guide' ? <Sparkles size={18} /> : <BookOpen size={18} />}
                      {article.type === 'how_to_guide' ? 'How-To Guide' : article.category}
                    </span>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3
                      className="text-lg font-semibold mb-2 line-clamp-2"
                      style={{ fontFamily: 'Fredoka', color: '#2D3748' }}
                    >
                      {article.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed mb-4 line-clamp-3 flex-1"
                      style={{ color: '#4A5568' }}
                    >
                      {article.seo_description || article.summary}
                    </p>
                    <span
                      className="inline-flex items-center gap-1 text-sm font-semibold"
                      style={{ color: '#C2410C' }}
                    >
                      Read full guide
                      <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/academy"
                data-testid="featured-academy-cta"
                className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-semibold btn-hover"
                style={{ background: 'linear-gradient(135deg, #9333EA, #FF8E71)', color: 'white' }}
              >
                <Sparkles size={16} />
                Explore the full Academy
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ────────────────────────────────────────────────────────────
          Phase 117 - Pricing comparison ("BYB vs the world")
          ────────────────────────────────────────────────────────────
          GEO signal: AI engines cite price-comparison content heavily
          because users ask "Is X worth it?". This table renders three
          rows (Build Your Book vs Story.com vs Print-on-Demand) with
          checkmark / cross indicators that crawlers can extract as
          *structured comparative claims*.
          ──────────────────────────────────────────────────────────── */}
      <section
        className="py-14"
        data-testid="pricing-comparison-section"
        aria-label="Build Your Book pricing vs alternatives"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug mb-3"
                style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
              How much does Build Your Book cost?
            </h2>
            <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: '#4A5568' }}>
              Personalized storybook plans on{' '}
              <strong>buildyourbook.in</strong> start at roughly{' '}
              <strong style={{ color: '#C2410C' }}>~₹50 / $0.5 per book</strong> -
              roughly <strong>1/10th</strong> the price of AI Storybook
              competitors, with 16 languages and cinematic MP4 included.
              1st Book is always free to new users.
            </p>
          </div>
          <div className="overflow-x-auto rounded-3xl border" style={{ borderColor: '#F3E8E0', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
            <table className="w-full bg-white text-sm" data-testid="pricing-comparison-table">
              <thead style={{ background: '#FFF7ED' }}>
                <tr>
                  <th className="text-left px-5 py-4 font-semibold" style={{ color: '#2D3748' }}>Feature</th>
                  <th className="text-center px-5 py-4 font-semibold" style={{ color: '#C2410C' }}>
                    Build Your Book<sup style={{ fontSize: '0.6em' }}>™</sup>
                  </th>
                  <th className="text-center px-5 py-4 font-semibold" style={{ color: '#4A5568' }}>
                    Competitor (AI Storybook)
                  </th>
                  <th className="text-center px-5 py-4 font-semibold" style={{ color: '#4A5568' }}>
                    Print-on-Demand (AI Storybook)
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Starting price', byb: '~₹50 / $0.5 per Book', alt1: '~₹285 / $3 per Book', alt2: '~₹2300 / $24 per book' },
                  { label: 'AI-illustrated chapters', byb: true, alt1: true, alt2: true },
                  { label: 'AI narration (audiobook)', byb: true, alt1: false, alt2: false },
                  { label: 'Cinematic MP4 video', byb: true, alt1: false, alt2: false },
                  { label: 'Cloned-voice narration', byb: true, alt1: false, alt2: false },
                  { label: '16 native languages', byb: true, alt1: false, alt2: false },
                  { label: 'Child-safe', byb: true, alt1: 'unclear', alt2: 'unclear' },
                  { label: 'INR + UPI payments', byb: true, alt1: false, alt2: false },
                  { label: 'Resale on marketplace', byb: true, alt1: false, alt2: false },
                ].map((row, idx) => (
                  <tr
                    key={row.label}
                    style={{ background: idx % 2 ? '#FFFDF9' : 'white', borderTop: '1px solid #F3E8E0' }}
                    data-testid={`pricing-row-${idx}`}
                  >
                    <td className="px-5 py-3 font-medium" style={{ color: '#2D3748' }}>{row.label}</td>
                    {[row.byb, row.alt1, row.alt2].map((cell, ci) => (
                      <td key={ci} className="px-5 py-3 text-center" style={{ color: '#4A5568' }}>
                        {cell === true ? (
                          <CheckCircle2 size={20} className="inline" style={{ color: '#10B981' }} />
                        ) : cell === false ? (
                          <XCircle size={20} className="inline" style={{ color: '#D1D5DB' }} />
                        ) : (
                          <span className={ci === 0 ? 'font-semibold' : ''} style={{ color: ci === 0 ? '#FF8E71' : '#4A5568' }}>
                            {cell}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-center text-xs mt-4" style={{ color: '#718096' }}>
            Competitor pricing as of Feb 2026. Build Your Book pricing applies to all{' '}
            <Link to="/subscription" className="underline">subscription plans</Link>{' '}
            (Starter ₹249, Premium, Legendary).
          </p>

          {/* Phase 119.13 - ExplorerHighlight strip removed. The new
              "Explorer ~₹50 / $0.5" hero card inside the "Why Creators
              Love Us" features section above carries the same CTA in
              a more conversion-friendly layout, so the small strip
              here was redundant and visually dilutive. */}

          {/* Phase 117.15 - calculator nested INSIDE the comparison
              section so the two render as ONE pricing surface (no
              section-padding gap between them). The CostCalculator
              keeps its `my-12` because we use a negative-top wrapper
              below to neutralise the redundant 48px above. */}
          <div
            className="-mt-6"
            data-testid="cost-calculator-section"
            aria-labelledby="cost-calculator-heading"
          >
            <h3 id="cost-calculator-heading" className="sr-only">
              Build Your Book - per-book compute cost calculator
            </h3>
            <CostCalculator variant="landing" />
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────
          Phase 118.0 - Homepage FAQ + "What parents ask next" presented
          side-by-side to compress vertical scroll and surface twice the
          Q&A density above the fold. Mirrors the FAQPage JSON-LD baked
          into index.html so JS-render crawlers (Perplexity browse,
          Googlebot Smartphone) see all Q&As in the rendered DOM. Every
          answer cements the "Build Your Book" → "buildyourbook.in"
          association.
          ──────────────────────────────────────────────────────────── */}
      <section
        className="py-14"
        style={{ background: '#FFF9F0' }}
        id="faq"
        aria-label="Frequently asked questions about Build Your Book"
        data-testid="homepage-faq-block"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12">
            {/* ── Left column: Frequently asked questions ── */}
            <div data-testid="homepage-faq-section" aria-label="Frequently asked questions">
              <div className="mb-6">
                <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug mb-3"
                    style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
                  Frequently asked questions
                </h2>
                <p className="text-sm leading-relaxed" style={{ color: '#4A5568' }}>
                  The short answers parents most often look for before creating their child&apos;s
                  first <strong>Build Your Book</strong> storybook. Synced live with our{' '}
                  <Link to="/faq" className="underline" style={{ color: '#C2410C' }}>full FAQ page</Link>.
                </p>
              </div>
              <dl className="space-y-3">
            {(homepageFaq.length > 0 ? homepageFaq : [
              {
                q: 'What is Build Your Book?',
                a: <>Build Your Book is an AI-powered personalized children&apos;s storybook marketplace. Parents and teachers create custom illustrated stories in 16 languages, with their own child as the named, illustrated hero. The official website is <a href="https://buildyourbook.in/" className="underline" style={{ color: '#C2410C' }}>buildyourbook.in</a>.</>,
              },
              {
                q: 'Is Build Your Book safe for children?',
                a: <>Yes. Every storybook is <Link to="/safety" className="underline" style={{ color: '#C2410C' }}>triple-checked</Link>: pre-generation moderation by Gemini 2.5 Flash, persona-aligned generation tuned to Grandma Jayshree&apos;s voice, and human admin review. Build Your Book is DPDP-ready and COPPA-conscious.</>,
              },
              {
                q: 'How much does Build Your Book cost?',
                a: <>Five plans on <strong>buildyourbook.in</strong>: <strong style={{ color: '#C2410C' }}>Explorer</strong> (pay-per-story ~<strong>~₹50 / $0.5</strong>, no subscription needed - start at{' '}<Link to="/create" className="underline" style={{ color: '#C2410C' }}>/create</Link>), <strong>Surviving</strong>, <strong>Thriving</strong>, <strong>Iconic</strong>, and <strong>Legendary</strong> subscriptions on{' '}<Link to="/subscription" className="underline" style={{ color: '#C2410C' }}>/subscription</Link>. Premium tiers add unlimited storybooks, voice cloning, cinematic MP4 video, and Kindle (KDP) export. 1st storybook is always free to new users. Payments via Razorpay LIVE in INR (India) or USD (international, live FX + 4% margin).</>,
              },
              {
                q: 'Who founded Build Your Book?',
                a: <>Build Your Book was founded by <Link to="/founder" className="underline" style={{ color: '#C2410C' }}>Priyal Dhrumit Shah</Link>, an electrical engineer and trainer from Gujarat, India. She was inspired by her mother-in-law Jayshree Jayesh Shah (Grandma Jayshree), a teacher and child-development specialist of 35+ years.</>,
              },
              {
                q: 'What languages does Build Your Book support?',
                a: <>16 native languages on buildyourbook.in: English, Hindi, Bengali, Gujarati, Marathi, Tamil, Telugu, Kannada, Malayalam, Punjabi, Urdu, Spanish, French, German, Japanese, and Chinese. Stories are generated natively in the chosen language.</>,
              },
              {
                q: 'How long does it take to create a storybook?',
                a: <>Under 5 minutes from start to finished book. You enter your child&apos;s name, age, language, theme and any references on{' '}<Link to="/create" className="underline" style={{ color: '#C2410C' }}>buildyourbook.in/create</Link>. Build Your Book&apos;s AI writes, illustrates, narrates, and packages the whole storybook into a flipbook, audiobook, and MP4 video - ready to download.</>,
              },
            ]).map((item, idx) => (
              <div
                key={item.q}
                className="bg-white rounded-2xl border px-6 py-4"
                style={{ borderColor: '#F3E8E0', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}
                data-testid={`faq-item-${idx}`}
              >
                <dt>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                  aria-expanded={openFaq === idx}
                  data-testid={`faq-toggle-${idx}`}
                  className="w-full cursor-pointer flex justify-between items-center font-semibold text-base text-left"
                  style={{ fontFamily: 'Fredoka', color: '#2D3748' }}
                >
                  <span className="flex items-center gap-2">
                    <HelpCircle size={18} style={{ color: '#C2410C' }} />
                    {item.q}
                  </span>
                  <span
                    aria-hidden="true"
                    className={`transition-transform duration-200 text-xl ${openFaq === idx ? 'rotate-45' : ''}`}
                    style={{ color: '#C2410C' }}
                  >+</span>
                </button>
                </dt>
                <dd
                  className={`mt-3 text-sm leading-relaxed whitespace-pre-line ${openFaq === idx ? '' : 'hidden'}`}
                  style={{ color: '#4A5568' }}
                >
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
              <div className="mt-6">
                <Link
                  to="/faq"
                  data-testid="homepage-faq-cta"
                  className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
                  style={{ color: '#C2410C' }}
                >
                  See all FAQs
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* ── Right column: What parents ask next ──────────────────
                GEO best-practice: AI engines surface "Related questions"
                / "People also ask" content when generating answers. This
                block gives them 8 inline-expandable follow-ups - each
                answer is rendered IN PLACE (no redirect) so the AI engine
                can extract the full Q+A in a single page snapshot.
                ────────────────────────────────────────────────────── */}
            <div
              data-testid="parents-ask-next-section"
              aria-label="Related questions parents ask about Build Your Book"
            >
              <div className="mb-6">
                <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug mb-3"
                    style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
                  What parents ask next
                </h2>
                <p className="text-sm leading-relaxed" style={{ color: '#4A5568' }}>
                  Related questions about <strong>Build Your Book</strong> - most are answered in{' '}
                  <Link to="/faq" className="underline" style={{ color: '#C2410C' }}>our full FAQ</Link>.
                </p>
              </div>
              <ul className="space-y-3" data-testid="parents-ask-next-list">
            {[
              {
                q: 'Can I clone my own voice on Build Your Book?',
                a: <>Yes - on the <strong>Premium</strong> and <strong>Legendary</strong> plans, you can record a short sample (about 30 seconds) and Build Your Book will narrate every storybook in your own voice. Perfect for grandparents reading to grandchildren from a distance, or for working parents who want their voice on every bedtime story.</>,
              },
              {
                q: 'Is Build Your Book safe for kids under 5?',
                a: <>Absolutely. Every storybook is <strong>triple-checked child-safe</strong>: (1) pre-generation moderation by Gemini 2.5 Flash, (2) persona-aligned generation tuned to Grandma Jayshree&apos;s warm voice, (3) human admin review before any book appears in the marketplace. The reading experience itself (large flipbook, gentle narration, no ads, no external links) is designed for under-5 attention spans.</>,
              },
              {
                q: 'How many books can I create per month?',
                a: <><strong>Starter</strong> &amp; <strong>Premium</strong> includes 5-30 storybooks per month by plan, and for <strong>Legendary</strong> is unlimited. Every new user gets the 1st Book free with no credit card required - so you can try Build Your Book magic before you pick a plan.</>,
              },
              {
                q: 'Does Build Your Book support Hindi storybooks?',
                a: <>Yes - Hindi is one of our 16 native languages. Stories are written natively in Devanagari (not machine-translated), illustrated with culturally anchored characters and settings, and narrated by an Indian-English-trained AI voice. We also support Bengali, Gujarati, Marathi, Tamil, Telugu, Kannada, Malayalam, Punjabi and Urdu the same way.</>,
              },
              {
                q: 'Can I sell my AI-generated storybooks on the marketplace?',
                a: <>Yes. Once a book passes our Triple-Checked Child-Safe review, you can list it on the Build Your Book Marketplace at a price you set. Every sale credits your creator wallet (INR or USD), and you keep ownership of the book - you can keep selling, gift it, or take it off the marketplace any time.</>,
              },
              {
                q: 'How do I gift a Build Your Book storybook?',
                a: <>You can buy any marketplace storybook directly as a gift - at checkout, choose &quot;Gift this book&quot;, enter the recipient&apos;s email, and Build Your Book emails them a personalised gift link with the cover, your message, and one-tap access to read, listen, or download as MP4/PDF.</>,
              },
              {
                q: 'What payment methods does Build Your Book accept?',
                a: <>Build Your Book accepts UPI, credit cards, debit cards, net-banking, and digital wallets via <strong>Razorpay</strong> (LIVE). Indian customers pay in INR, international customers in USD. No subscription auto-renews without explicit consent - you can cancel any time from your profile.</>,
              },
              {
                q: 'Can I download my Build Your Book storybook as a PDF or MP4?',
                a: <>Yes - every storybook on Build Your Book comes with three formats included: a <strong>flipbook PDF</strong> (print-ready), an <strong>audiobook MP3</strong> (narrated), and a <strong>cinematic MP4 video</strong> (illustrations + narration combined). All three are yours to keep forever, even if you cancel your subscription.</>,
              },
            ].map((item, idx) => (
              <li key={item.q}>
                <details
                  className="group bg-white border rounded-2xl px-4 py-3 hover:shadow-md transition-shadow"
                  style={{ borderColor: '#F3E8E0' }}
                  data-testid={`parents-ask-${idx}`}
                >
                  <summary
                    className="cursor-pointer list-none flex items-center gap-3"
                    style={{ color: '#2D3748' }}
                  >
                    <HelpCircle size={16} style={{ color: '#9333EA' }} className="shrink-0" />
                    <span className="text-sm font-medium flex-1">
                      {item.q}
                    </span>
                    <ArrowRight
                      size={14}
                      style={{ color: '#C2410C' }}
                      className="shrink-0 transition-transform duration-200 group-open:rotate-90"
                      aria-hidden="true"
                    />
                  </summary>
                  <p
                    className="mt-3 text-sm leading-relaxed border-t pt-3"
                    style={{ color: '#4A5568', borderColor: '#FBEDE2' }}
                  >
                    {item.a}
                  </p>
                </details>
              </li>
            ))}
          </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Top Stories of the Month - public leaderboard */}
      <LeaderboardSection />

      {/* CTA Section */}
      <section className="py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight mb-6" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
            Ready to Share Your Story?
          </h2>
          <p className="text-lg leading-relaxed mb-8" style={{ color: '#4A5568' }}>
            Join thousands of storytellers creating magical experiences for readers worldwide
          </p>
          <Link to="/login" data-testid="cta-get-started-button">
            <button className="inline-flex items-center justify-center rounded-full px-8 py-3.5 font-semibold btn-hover" style={{ background: '#C2410C', color: 'white' }}>
              Get Started for Free
            </button>
          </Link>

          {/* "A World of Stories Awaits" illustration - soft pastel scene
              of two children reading, anchoring the CTA emotionally.
              `aria-hidden` because the section heading already conveys
              the meaning to screen readers, so the image is purely
              decorative here. Lazy-loaded since it sits well below the
              fold and doesn't compete with the LCP element. */}
          <div className="mt-12 flex justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_story-forge-shop/artifacts/zmwcm6xa_image_3d0d8735.png"
              alt="Two children reading a Build Your Book personalized storybook together - an illustrated pastel scene capturing the warmth of bedtime stories created on buildyourbook.in."
              loading="lazy"
              className="rounded-3xl shadow-2xl w-full max-w-2xl"
              style={{ boxShadow: '0 24px 60px rgba(45, 55, 72, 0.15)' }}
              data-testid="cta-stories-await-image"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t" style={{ borderColor: '#F3E8E0' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="flex flex-wrap justify-center gap-6 mb-4 text-sm" style={{ color: '#4A5568' }}>
            <Link to="/safety" data-testid="footer-safety-link" className="inline-flex items-center gap-1.5 hover:underline" style={{ color: '#1B5E20', fontWeight: 600 }}>
              <ShieldCheck size={14} />
              Child-Safe Promise
            </Link>
            <Link to="/marketplace" className="hover:underline" data-testid="footer-marketplace-link">
              Browse Stories
            </Link>
            <Link to="/academy" className="hover:underline" data-testid="footer-academy-link">
              Academy
            </Link>
            <Link to="/privacy" className="hover:underline" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:underline" data-testid="footer-terms-link">
              Terms &amp; Conditions
            </Link>
            <Link to="/faq" className="hover:underline" data-testid="footer-faq-link">
              FAQ
            </Link>
            <Link to="/subscription" className="hover:underline" data-testid="footer-pricing-link">
              Pricing
            </Link>
            <Link to="/founder" className="hover:underline" data-testid="footer-founder-link">
              Founder
            </Link>
          </div>

          {/* Contact + Social - admin-editable via /api/admin/settings/contact.
              Rendered only when the URL/email is non-empty so a cleared
              field doesn't leave a dead icon behind. */}
          <div className="flex flex-col items-center gap-3 mb-6">
            <p className="text-sm font-semibold" style={{ color: '#4A5568', fontFamily: 'Fredoka' }}>
              Visit us &amp; Contact
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              {contact.instagram_url && (
                <a
                  href={contact.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  className="inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full btn-hover"
                  style={{ background: 'linear-gradient(135deg, #F58529, #DD2A7B, #8134AF)', color: 'white', fontWeight: 600 }}
                  aria-label="Instagram"
                >
                  {/* Brand-accurate Instagram "camera" mark. Matches the
                      Facebook chip construction - white circle backdrop
                      with brand-coloured glyph cutout. Renders 22 px so
                      every pill in the row is visually weighted equally. */}
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <rect width="24" height="24" rx="6" fill="white" />
                    <path
                      d="M8.4 4.5h7.2c2.16 0 3.9 1.74 3.9 3.9v7.2c0 2.16-1.74 3.9-3.9 3.9H8.4c-2.16 0-3.9-1.74-3.9-3.9V8.4c0-2.16 1.74-3.9 3.9-3.9Zm0 1.5A2.4 2.4 0 0 0 6 8.4v7.2A2.4 2.4 0 0 0 8.4 18h7.2a2.4 2.4 0 0 0 2.4-2.4V8.4A2.4 2.4 0 0 0 15.6 6H8.4ZM12 7.95a4.05 4.05 0 1 1 0 8.1 4.05 4.05 0 0 1 0-8.1Zm0 1.5a2.55 2.55 0 1 0 0 5.1 2.55 2.55 0 0 0 0-5.1Zm4.5-2.4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
                      fill="url(#ig-grad)"
                    />
                    <defs>
                      <linearGradient id="ig-grad" x1="4" y1="20" x2="20" y2="4" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#F58529" />
                        <stop offset="0.5" stopColor="#DD2A7B" />
                        <stop offset="1" stopColor="#8134AF" />
                      </linearGradient>
                    </defs>
                  </svg>
                  Instagram
                </a>
              )}
              {contact.youtube_url && (
                <a
                  href={contact.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-youtube-link"
                  className="inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full btn-hover"
                  style={{ background: '#FF0000', color: 'white', fontWeight: 600 }}
                  aria-label="YouTube"
                >
                  {/* Brand-accurate YouTube "play in red rounded square"
                      mark. White rounded-rect backdrop + red play arrow
                      cutout, sized 22 px to match the Facebook chip. */}
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <rect width="24" height="24" rx="6" fill="white" />
                    <path
                      d="M4.8 9.4c0-1.39 0-2.08.27-2.6a2.4 2.4 0 0 1 1.05-1.05c.52-.27 1.21-.27 2.6-.27h6.56c1.39 0 2.08 0 2.6.27.46.24.81.59 1.05 1.05.27.52.27 1.21.27 2.6v5.2c0 1.39 0 2.08-.27 2.6a2.4 2.4 0 0 1-1.05 1.05c-.52.27-1.21.27-2.6.27H8.72c-1.39 0-2.08 0-2.6-.27a2.4 2.4 0 0 1-1.05-1.05c-.27-.52-.27-1.21-.27-2.6V9.4Zm5.6-.4v6l5.2-3-5.2-3Z"
                      fill="#FF0000"
                    />
                  </svg>
                  YouTube
                </a>
              )}
              {contact.facebook_url && (
                <a
                  href={contact.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-facebook-link"
                  className="inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full btn-hover"
                  style={{ background: '#0866FF', color: 'white', fontWeight: 600 }}
                  aria-label="Facebook"
                >
                  {/* Official Facebook "f-in-circle" brand mark. We
                      draw the WHITE circle backdrop + the brand-blue
                      "f" cutout inline so it stays crisp at any size
                      and matches the brand-guidelines reference image
                      the user provided. Lucide's outline `Facebook`
                      icon was too thin and didn't read as the brand. */}
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="12" fill="white" />
                    <path
                      d="M13.36 22.95V13.3h2.71l.4-3.15h-3.11V8.14c0-.91.25-1.53 1.56-1.53h1.67V3.8a22.4 22.4 0 0 0-2.43-.13c-2.4 0-4.05 1.47-4.05 4.16v2.32H7.4v3.15h2.71V22.95a11 11 0 1 0 3.25 0Z"
                      fill="#0866FF"
                    />
                  </svg>
                  Facebook
                </a>
              )}
              {contact.twitter_url && (
                <a
                  href={contact.twitter_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-x-link"
                  className="inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full btn-hover"
                  style={{ background: '#000000', color: 'white', fontWeight: 600 }}
                  aria-label="X (Twitter)"
                >
                  {/* Official "X" mark inside a white-rounded square to
                      match the brand-guideline reference image. Drawn
                      inline so it stays crisp + matches the IG/FB chip
                      pattern already in the footer. */}
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <rect width="24" height="24" rx="5" fill="#000000" />
                    <path
                      d="M16.99 5.5h2.18l-4.76 5.44L20 18.5h-4.39l-3.44-4.5-3.94 4.5H6.05l5.09-5.82L5.6 5.5h4.5l3.11 4.11L16.99 5.5Zm-.77 11.62h1.21L9.84 6.79H8.55l7.67 10.33Z"
                      fill="#FFFFFF"
                    />
                  </svg>
                  X
                </a>
              )}
              {contact.pinterest_url && (
                <a
                  href={contact.pinterest_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-pinterest-link"
                  className="inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full btn-hover"
                  style={{ background: '#E60023', color: 'white', fontWeight: 600 }}
                  aria-label="Pinterest"
                >
                  {/* Brand-accurate Pinterest "P" mark. White circle
                      backdrop + red "P" cutout, same construction as
                      the Facebook chip. */}
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="12" fill="#E60023" />
                    <path
                      d="M12 4.5a7.5 7.5 0 0 0-2.73 14.49c-.07-.6-.13-1.53.03-2.19l.86-3.66s-.22-.44-.22-1.1c0-1.03.6-1.8 1.34-1.8.63 0 .94.47.94 1.04 0 .64-.4 1.6-.61 2.48-.18.74.37 1.34 1.1 1.34 1.32 0 2.34-1.4 2.34-3.41 0-1.78-1.28-3.03-3.12-3.03-2.12 0-3.37 1.59-3.37 3.24 0 .64.25 1.33.55 1.7.06.07.07.14.05.21l-.21.85c-.03.13-.11.16-.25.1-.92-.43-1.5-1.78-1.5-2.86 0-2.32 1.69-4.45 4.87-4.45 2.56 0 4.54 1.82 4.54 4.26 0 2.54-1.6 4.59-3.82 4.59-.75 0-1.45-.39-1.69-.85l-.46 1.76c-.17.64-.62 1.43-.92 1.92A7.5 7.5 0 1 0 12 4.5Z"
                      fill="#FFFFFF"
                    />
                  </svg>
                  Pinterest
                </a>
              )}
              {contact.support_email && (
                <a
                  href={`mailto:${contact.support_email}`}
                  data-testid="footer-email-link"
                  className="inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full btn-hover"
                  style={{ background: '#1B5E20', color: 'white', fontWeight: 600 }}
                  aria-label="Email"
                >
                  {/* Envelope glyph on a white rounded-square backdrop,
                      matching the FB / X / Pinterest construction so the
                      whole row reads as one tidy chip set. */}
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <rect width="24" height="24" rx="5" fill="white" />
                    <path
                      d="M5.4 7.2h13.2c.66 0 1.2.54 1.2 1.2v7.2c0 .66-.54 1.2-1.2 1.2H5.4a1.2 1.2 0 0 1-1.2-1.2V8.4c0-.66.54-1.2 1.2-1.2Zm0 1.5v.18l6.6 4.62 6.6-4.62V8.7H5.4Zm13.2 1.92-6.18 4.33a.6.6 0 0 1-.84 0L5.4 10.62V15.6h13.2v-4.98Z"
                      fill="#1B5E20"
                    />
                  </svg>
                  {contact.support_email}
                </a>
              )}
            </div>
          </div>

          <p className="text-sm" style={{ color: '#718096' }} data-testid="footer-copyright">© 2026 Build Your Book<sup className="text-[9px]" style={{ color: '#A68461', marginLeft: '1px' }}>TM</sup>, by OrcSyn Intelligent Technologies.</p>

          {/* Razorpay trust pill - single logo-only chip matched to the
              social-media pill chrome (22 px tall, rounded, black bg so
              the official Razorpay wordmark + blue arrow render with
              maximum contrast). No supporting text per spec - the
              well-known Razorpay logo is the trust signal on its own. */}
          <a
            href="https://razorpay.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-razorpay-trust"
            aria-label="Powered by Razorpay"
            className="mt-3 inline-flex items-center justify-center px-3 py-1.5 rounded-full btn-hover"
            style={{ background: '#000000' }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_613314a9-4f3a-4865-bec4-3c2b82a7ccee/artifacts/ce0gwmxw_image.png"
              alt="Razorpay"
              height="22"
              style={{ height: '22px', width: 'auto', display: 'block' }}
              loading="lazy"
            />
          </a>
        </div>
      </footer>

      {/* Phase 86 - Emergent-style floating "What's new" teaser.
          Lives only on the landing page. Opens a modal with all
          active announcements. Reads the same /api/announcements
          feed the dashboard marquee used to read. */}
      <LandingAnnouncementsTeaser />
    </div>
  );
}

export default LandingPage;
